// ./src/components/features/PartnerPackages/PartnerPackageEditDrawer/PartnerPackageEditDrawer.tsx
//
// Create/edit drawer for a PartnerPackage (account-level reusable partner/sponsor
// offer). Self-loading in edit mode via recordId; context-or-props resolution; no
// success toast (the page highlight is the confirmation). Fields: partner kind,
// name, description, benefits (repeatable list), money direction, default amount
// + currency, status.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  NumberInput,
  Group,
  Divider,
  Skeleton,
  ActionIcon,
  Input,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  PartnerPackage,
  PartnerPackageInput,
  PARTNER_KIND_VALUES,
  PARTNER_MONEY_DIRECTION_VALUES,
  PARTNER_MONEY_DIRECTION,
  PARTNER_KIND_LABELS,
  PARTNER_MONEY_DIRECTION_LABELS,
} from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalPartnerPackagesContext } from "@/contexts/PartnerPackagesContext";
import { AppDrawer, StatusInput, CurrencySelector, AppIcon, SelectField } from "@/components/generic";

export interface PartnerPackageEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode. */
  initialName?: string | null;
  onCreated?: (pkg: PartnerPackage) => void;
  onUpdated?: (pkg: PartnerPackage) => void;
}

interface FormData {
  partnerKind: string;
  name: string;
  description: string;
  benefits: string[];
  moneyDirection: string;
  defaultAmount: number | "";
  currencyCode: string | null;
  status: number;
}

const KNOWN_FORM_FIELDS = [
  "partnerKind",
  "name",
  "description",
  "benefits",
  "moneyDirection",
  "defaultAmount",
  "currencyCode",
  "status",
];

const getInitialFormData = (): FormData => ({
  partnerKind: "",
  name: "",
  description: "",
  benefits: [""],
  moneyDirection: PARTNER_MONEY_DIRECTION.REVENUE,
  defaultAmount: "",
  currencyCode: null,
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: PartnerPackage): FormData => ({
  partnerKind: record.partnerKind || "",
  name: record.name || "",
  description: record.description || "",
  benefits: withTrailingEmptyBenefits(Array.isArray(record.benefits) ? record.benefits : []),
  moneyDirection: record.moneyDirection || PARTNER_MONEY_DIRECTION.REVENUE,
  defaultAmount: record.defaultAmount ?? "",
  currencyCode: record.currencyCode || null,
  status: record.status ?? STATUSES.ACTIVE,
});

/**
 * Keep exactly one trailing empty benefit row: append a fresh empty string when the
 * last one has text, and seed a single empty row when the list is empty. Typing in
 * the last row is what spawns the next one — no explicit "Add" button (mirrors the
 * poll/RSVP option editors). Empty rows are dropped on save.
 */
const withTrailingEmptyBenefits = (benefits: string[]): string[] => {
  if (benefits.length === 0) {
    return [""];
  }

  const last = benefits[benefits.length - 1];

  return last.trim() !== "" ? [...benefits, ""] : benefits;
};

const serialize = (data: FormData): string => JSON.stringify(data);

const PartnerPackageEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={80} radius="sm" />
  </Stack>
);

export const PartnerPackageEditDrawer: React.FC<PartnerPackageEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["partnerPackages", "common"]);

  const ctx = useOptionalPartnerPackagesContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<PartnerPackage | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as PartnerPackage);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record)
      : { ...getInitialFormData(), name: initialName?.trim() || "" };
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Benefits list editing (auto-append trailing empty row; no "Add" button) ──
  const setBenefit = useCallback((index: number, value: string) => {
    setFormData((prev) => {
      const benefits = [...prev.benefits];
      benefits[index] = value;

      return { ...prev, benefits: withTrailingEmptyBenefits(benefits) };
    });
  }, []);

  const removeBenefit = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      benefits: withTrailingEmptyBenefits(prev.benefits.filter((_, i) => i !== index)),
    }));
  }, []);

  // ── Options ──
  const kindOptions = useMemo(
    () => PARTNER_KIND_VALUES.map((v) => ({ value: v, label: t(`partnerPackages:kind.${v}`, { defaultValue: PARTNER_KIND_LABELS[v] }) })),
    [t]
  );

  const moneyOptions = useMemo(
    () =>
      PARTNER_MONEY_DIRECTION_VALUES.map((v) => ({
        value: v,
        label: t(`partnerPackages:money.${v}`, { defaultValue: PARTNER_MONEY_DIRECTION_LABELS[v] }),
      })),
    [t]
  );

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const cleanedBenefits = formData.benefits.map((b) => b.trim()).filter((b) => b.length > 0);

    const submitData: PartnerPackageInput = {
      partnerKind: formData.partnerKind || null,
      name: formData.name.trim() || null,
      description: formData.description.trim() || null,
      benefits: cleanedBenefits,
      moneyDirection: formData.moneyDirection || null,
      defaultAmount: formData.defaultAmount === "" ? null : Number(formData.defaultAmount),
      currencyCode: formData.currencyCode,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as PartnerPackage[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("partnerPackages:form.editTitle", { defaultValue: "Edit package" })
    : t("partnerPackages:form.addTitle", { defaultValue: "Add package" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <PartnerPackageEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <SelectField
            label={t("partnerPackages:form.fields.partnerKind.label", { defaultValue: "Partner type" })}
            description={t("partnerPackages:form.fields.partnerKind.description", {
              defaultValue: "What kind of partner this offer is for.",
            })}
            placeholder={t("partnerPackages:form.fields.partnerKind.placeholder", { defaultValue: "Select a type" })}
            data={kindOptions}
            value={formData.partnerKind || null}
            onChange={(_name, value) => setField("partnerKind", value ?? "")}
            error={fieldErrors.partnerKind}
            data-autofocus
            allowDeselect={false}
            size="md"
          />

          <TextInput
            label={t("partnerPackages:form.fields.name.label", { defaultValue: "Name" })}
            description={t("partnerPackages:form.fields.name.description", {
              defaultValue: "The offer's name, shown wherever you use it.",
            })}
            placeholder={t("partnerPackages:form.fields.name.placeholder", { defaultValue: "e.g. Gold Sponsor" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            size="md"
          />

          <Textarea
            label={t("partnerPackages:form.fields.description.label", { defaultValue: "Description" })}
            placeholder={t("partnerPackages:form.fields.description.placeholder", {
              defaultValue: "What this package is about…",
            })}
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={6}
            size="md"
          />

          <Input.Wrapper
            label={t("partnerPackages:form.fields.benefits.label", { defaultValue: "Benefits" })}
            description={t("partnerPackages:form.fields.benefits.description", {
              defaultValue: "What the package includes — one line per benefit.",
            })}
            error={fieldErrors.benefits}
          >
            <Stack gap="xs" mt={6}>
              {formData.benefits.map((benefit, index) => {
                // The single trailing empty row is a placeholder for the next benefit —
                // no remove button (there's nothing to remove yet).
                const isTrailingEmpty = index === formData.benefits.length - 1 && benefit.trim() === "";

                return (
                  <Group key={index} gap="xs" wrap="nowrap" align="center">
                    <TextInput
                      style={{ flex: 1 }}
                      placeholder={t("partnerPackages:form.fields.benefits.itemPlaceholder", {
                        defaultValue: "e.g. Logo on the main stage",
                      })}
                      value={benefit}
                      onChange={(e) => setBenefit(index, e.currentTarget.value)}
                      size="md"
                    />
                    {isTrailingEmpty ? (
                      <span style={{ width: 34, flexShrink: 0 }} />
                    ) : (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="lg"
                        onClick={() => removeBenefit(index)}
                        aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                      >
                        <AppIcon iconName="Trash" size={18} />
                      </ActionIcon>
                    )}
                  </Group>
                );
              })}
            </Stack>
          </Input.Wrapper>

          <Divider
            label={t("partnerPackages:form.sections.pricing", { defaultValue: "Pricing (optional)" })}
            labelPosition="left"
          />

          <SelectField
            label={t("partnerPackages:form.fields.moneyDirection.label", { defaultValue: "Money direction" })}
            description={t("partnerPackages:form.fields.moneyDirection.description", {
              defaultValue: "Whether this package brings revenue in or is an expense.",
            })}
            data={moneyOptions}
            value={formData.moneyDirection || null}
            onChange={(_name, value) => setField("moneyDirection", value ?? PARTNER_MONEY_DIRECTION.REVENUE)}
            error={fieldErrors.moneyDirection}
            allowDeselect={false}
            size="md"
          />

          <Group grow align="flex-start">
            <NumberInput
              label={t("partnerPackages:form.fields.defaultAmount.label", { defaultValue: "Default amount" })}
              description={t("partnerPackages:form.fields.defaultAmount.description", {
                defaultValue: "Overridable per event.",
              })}
              placeholder="0.00"
              value={formData.defaultAmount}
              onChange={(value) => setField("defaultAmount", value === "" ? "" : (value as number))}
              error={fieldErrors.defaultAmount}
              min={0}
              decimalScale={2}
              step={0.01}
              thousandSeparator=","
              size="md"
            />

            <CurrencySelector
              label={t("partnerPackages:form.fields.currencyCode.label", { defaultValue: "Currency" })}
              value={formData.currencyCode}
              onChange={(_name, value) => setField("currencyCode", value)}
              required={false}
              error={fieldErrors.currencyCode}
              isoOnly
              withinPortal
              size="md"
            />
          </Group>

          <Divider />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
